<script setup>
import { computed } from 'vue';
import { formatSignedPercent, signClass, toNumber } from '../format';
import { dayChange } from '../heatmap';
import { intlLocale, t } from '../i18n';
import { breadth } from '../marketOverview';
import InfoHint from './InfoHint.vue';
import { PANEL } from '../ui';

// «Рынок сегодня» (docs/market-overview/SPEC.md, decision 2): RVI with its day change and the breadth
// of IMOEX shares as a bar. The key rate joins it in stage 3.
const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  rvi: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const rviValue = computed(() => {
  const n = toNumber(props.rvi?.price);
  return n === null ? '—' : new Intl.NumberFormat(intlLocale(), {
    minimumFractionDigits: props.rvi.decimals,
    maximumFractionDigits: props.rvi.decimals,
  }).format(n);
});

// Two digits, as the quotes; a change that rounds to zero is grey.
const rviChange = computed(() => {
  const change = dayChange(props.rvi?.price, props.rvi?.prev_close);
  return change === null ? null : Number(change.toFixed(2));
});

// RVI is fear: its growth is red, its fall green (decision 7), unlike the quotes.
const rviChangeClass = computed(() => signClass(rviChange.value === null ? null : -rviChange.value));

// Level in words (decision 8): bounds from RVI's daily closes since 2015 — median 29.7, half of the days
// in 23.5–36.8, nine of ten below 48.4.
const RVI_LEVELS = [
  { key: 'calm', below: 25, chip: 'bg-emerald-500/15 text-emerald-300' },
  { key: 'normal', below: 35, chip: 'bg-slate-700/60 text-slate-200' },
  { key: 'anxious', below: 50, chip: 'bg-amber-500/15 text-amber-300' },
  { key: 'panic', below: Infinity, chip: 'bg-rose-500/15 text-rose-300' },
];

const rviLevel = computed(() => {
  const n = toNumber(props.rvi?.price);
  return n === null ? null : RVI_LEVELS.find((level) => n < level.below);
});

const counts = computed(() => breadth(props.items));

const parts = computed(() => [
  { key: 'up', count: counts.value.up, bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
  { key: 'flat', count: counts.value.flat, bar: 'bg-slate-600', dot: 'bg-slate-500' },
  { key: 'down', count: counts.value.down, bar: 'bg-rose-500', dot: 'bg-rose-500' },
]);
</script>

<template>
  <section
    :class="PANEL"
    :aria-busy="loading ? 'true' : undefined"
  >
    <h2 class="text-[1.0625rem] font-semibold text-slate-100 sm:text-lg">
      {{ t('market.today.title') }}
    </h2>

    <div
      v-if="loading && !rvi"
      class="mt-3 space-y-3"
    >
      <div class="h-11 rounded bg-slate-800 motion-safe:animate-pulse" />
      <div class="h-14 rounded bg-slate-800 motion-safe:animate-pulse" />
    </div>

    <template v-else>
      <div class="mt-3 flex items-center justify-between gap-3">
        <div class="flex min-w-0 items-center gap-1.5 text-[0.9375rem] text-slate-100">
          <span class="truncate">{{ t('market.today.rvi') }}</span>
          <InfoHint :label="t('market.today.rvi')">
            {{ t('market.today.rviMore') }}
          </InfoHint>
        </div>
        <p class="flex shrink-0 items-baseline gap-3 text-[0.9375rem] tabular-nums">
          <span class="text-slate-100">{{ rviValue }}</span>
          <span
            class="w-20 text-right"
            :class="rviChangeClass"
          >{{ formatSignedPercent(rviChange, 2) }}</span>
        </p>
      </div>
      <!-- The scale with its source is behind «?» next to the level (user, 2026-10-24) -->
      <div
        v-if="rviLevel"
        class="mt-1.5 flex items-center gap-1.5"
      >
        <span
          class="inline-flex h-6 items-center rounded-full px-2.5 text-[0.8125rem] font-medium"
          :class="rviLevel.chip"
        >{{ t(`market.today.level.${rviLevel.key}`) }}</span>
        <InfoHint :label="t('market.today.levelAria')">
          <span
            v-for="level in RVI_LEVELS"
            :key="level.key"
            class="flex justify-between gap-3"
            :class="level.key === rviLevel.key ? 'font-semibold text-slate-100' : ''"
          >
            <span>{{ t(`market.today.level.${level.key}`) }}</span>
            <span class="tabular-nums">{{ t(`market.today.range.${level.key}`) }}</span>
          </span>
          <span class="mt-2 block border-t border-slate-700 pt-2">{{ t('market.today.rviScale') }}</span>
        </InfoHint>
      </div>
      <!-- Not everyone knows RVI (user, 2026-10-24): what it is, in full under the value -->
      <p class="mt-1.5 text-[0.8125rem] leading-snug text-slate-500">
        {{ t('market.today.rviHint') }}
      </p>

      <div class="mt-4 border-t border-slate-800 pt-4">
        <p class="text-[0.9375rem] text-slate-100">
          {{ t('market.today.breadth') }}
        </p>
        <p
          v-if="counts.total === 0"
          class="mt-1 text-[0.8125rem] text-slate-500"
        >
          {{ t('market.noData') }}
        </p>
        <template v-else>
          <!-- Widths follow the counts; screen readers get the counts from the legend below -->
          <div
            class="mt-2 flex h-2 gap-0.5 overflow-hidden rounded-full"
            aria-hidden="true"
          >
            <div
              v-for="part in parts.filter((p) => p.count > 0)"
              :key="part.key"
              :class="part.bar"
              :style="{ flexGrow: part.count }"
            />
          </div>
          <ul class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[0.8125rem] text-slate-400">
            <li
              v-for="part in parts"
              :key="part.key"
              class="flex items-center gap-1.5 tabular-nums"
            >
              <span
                class="size-2 rounded-full"
                :class="part.dot"
                aria-hidden="true"
              />
              {{ t(`market.today.${part.key}`, { count: part.count }) }}
            </li>
          </ul>
        </template>
      </div>
    </template>
  </section>
</template>
